import { MutationTree } from 'vuex';
import { SingleFilter } from '@/store/types';
import { ArticleState } from './types';
import { ArticleResource, PriceListResource, WarehouseResource, CategoryResource, ArticleRequest, ArticlePriceListResource } from '@/api/requests/article/types';

export const mutations: MutationTree<ArticleState> = {
    /** Articles */

    /**
     * Add an article to the list.
     *
     * @param state
     * @param article
     */
    addArticle(state, article: ArticleResource) {
        state.articles.push(article);
    },

    /**
     * Remove an article from the list.
     *
     * @param state
     * @param articleId
     */
    removeArticle(state, articleId: number) {
        state.articles = state.articles.filter((article) => article.id !== articleId);
    },

    /**
     * Set the articles list.
     *
     * @param state
     * @param articles
     */
    setArticles(state, articles: ArticleResource[]) {
        state.articles = articles;
    },


    /**
     * Reset articles
     *
     * @param state
     */
    resetArticles(state) {
        state.articles = [];
    },

    /**
     * Update an article.
     *
     * @param state
     * @param article
     */
    updateArticle(state, article: ArticleResource) {
        const index = state.articles.findIndex((item) => item.id === article.id);
        state.articles.splice(index, 1, article);
    },

    /**
     * Set the active task id.
     *
     * @param state
     * @param id
     */
    setActiveArticleId(state, id: number) {
        state.activeArticleId = id;
    },

    /**
     * Update a article.
     *
     * @param state
     * @param task
     */
    upsertArticle(state, article: ArticleResource) {
        const index = state.articles.findIndex((item) => item.id === article.id);
        if (index > -1) {
            state.articles.splice(index, 1, article);
        } else {
            state.articles.push(article);
        }
    },

    /** Price Lists */

    /**
     * Add a price list to the list.
     *
     * @param state
     * @param priceList
     */
    addPriceList(state, priceList: PriceListResource) {
        state.priceLists.push(priceList);
    },

    /**
     * Remove a price list from the list.
     *
     * @param state
     * @param articleId
     */
    removePriceList(state, priceListId: number) {
        state.priceLists = state.priceLists.filter((priceList) => priceList.id !== priceListId);
    },

    /**
     * Set the price list.
     *
     * @param state
     * @param priceLists
     */
    setPriceLists(state, priceLists: PriceListResource[]) {
        state.priceLists = priceLists;
    },


    /**
     * Reset price lists.
     *
     * @param state
     */
    resetPriceLists(state) {
        state.priceLists = [];
    },

    /**
     * Update a price list.
     *
     * @param state
     * @param priceList
     */
    updatePriceList(state, priceList: PriceListResource) {
        const index = state.priceLists.findIndex((item) => item.id === priceList.id);
        state.priceLists.splice(index, 1, priceList);
    },

    /**
     * Set the active price list id.
     *
     * @param state
     * @param id
     */
    setActivePriceListId(state, id: number) {
        state.activePriceListId = id;
    },

    /**
     * Update a price list.
     *
     * @param state
     * @param task
     */
    upsertPriceList(state, priceList: PriceListResource) {
        const index = state.priceLists.findIndex((item) => item.id === priceList.id);
        if (index > -1) {
            state.priceLists.splice(index, 1, priceList);
        } else {
            state.priceLists.push(priceList);
        }
    },

    /**
     * Set the active price list id.
     *
     * @param state
     * @param id
     */
    setArticlesPriceList(state, {priceList, articles}) {
        priceList.articles = articles;
    },

    /** Warehouses */

    /**
     * Add a warehouse to the list.
     *
     * @param state
     * @param priceList
     */
    addWarehouse(state, warehouse: WarehouseResource) {
        state.warehouses.push(warehouse);
    },

    /**
     * Remove a warehouse from the list.
     *
     * @param state
     * @param articleId
     */
    removeWarehouse(state, warehouseId: number) {
        state.warehouses = state.warehouses.filter((warehouse) => warehouse.id !== warehouseId);
    },

    /**
     * Set the warehouse list.
     *
     * @param state
     * @param warehouses
     */
    setWarehouses(state, warehouses: WarehouseResource[]) {
        state.warehouses = warehouses;
    },


    /**
     * Reset warehouses
     *
     * @param state
     */
    resetWarehouses(state) {
        state.warehouses = [];
    },

    /**
     * Update a warehouse.
     *
     * @param state
     * @param warehouse
     */
    updateWarehouse(state, warehouse: WarehouseResource) {
        const index = state.warehouses.findIndex((item) => item.id === warehouse.id);
        state.warehouses.splice(index, 1, warehouse);
    },

    /** Categories */

    /**
     * Add a category to the list.
     *
     * @param state
     * @param category
     */
    addCategory(state, category: CategoryResource) {
        state.categories.push(category);
    },

    /**
     * Remove a category from the list.
     *
     * @param state
     * @param categoryId
     */
    removeCategory(state, categoryId: number) {
        state.categories = state.categories.filter((category) => category.id !== categoryId);
    },

    /**
     * Set the categories list.
     *
     * @param state
     * @param categories
     */
    setCategories(state, categories: CategoryResource[]) {
        state.categories = categories;
    },


    /**
     * Reset categories.
     *
     * @param state
     */
    resetCategories(state) {
        state.categories = [];
    },

    /**
     * Update a category.
     *
     * @param state
     * @param category
     */
    updateCategory(state, category: CategoryResource) {
        const index = state.categories.findIndex((item) => item.id === category.id);
        state.categories.splice(index, 1, category);
    },

    /** Filters */

    /**
     * Set a query filter in state.
     *
     * @param state
     * @param key
     * @param value
     */
    setFilter(state, { key, value }: SingleFilter) {
        state.filters = { ...state.filters, [key]: value };
    },

    /**
     * Reset a single filter.
     *
     * @param state
     * @param key
     */
    resetFilter(state, key: string) {
        delete state.filters[key];
    },

    /**
     * Reset query filters.
     *
     * @param state
     */
    resetFilters(state) {
        state.filters = {};
    },
};
